/**
 * NamedAnchors.ts — case/separator-insensitive lookup for glb-harvested
 * named anchor points (e.g. `SkipperHead`, `anchor_rode_exit`).
 *
 * Author spellings vary (`Skipper.Head`, `skipper_head`, `SkipperHead`); we
 * normalise by lowercasing and stripping `. _ -` and whitespace before
 * comparison. Callers control priority by passing candidates in order.
 */

export type Vec3 = [number, number, number];
export type NamedAnchorBag = Record<string, Vec3> | undefined;

const normAnchorName = (name: string): string =>
  name.toLowerCase().replace(/[._\-\s]+/g, "");

export interface NamedAnchorHit {
  name: string;
  position: Vec3;
}

export function findNamedAnchor(
  bag: NamedAnchorBag,
  candidates: readonly string[],
): NamedAnchorHit | undefined {
  if (!bag) return undefined;
  const lookup = new Map<string, NamedAnchorHit>();
  for (const [name, pos] of Object.entries(bag)) {
    lookup.set(normAnchorName(name), {
      name,
      position: [pos[0], pos[1], pos[2]],
    });
  }
  for (const candidate of candidates) {
    const hit = lookup.get(normAnchorName(candidate));
    if (hit) return hit;
  }
  return undefined;
}

export function pickNamedAnchor(
  bag: NamedAnchorBag,
  candidates: readonly string[],
): Vec3 | undefined {
  return findNamedAnchor(bag, candidates)?.position;
}
